import { doc, setDoc, getDoc, collection, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { getUserPath, getUserProjectsPath, getUserProjectPath, getProjectPath } from '../utils/collectionPaths';

/**
 * Create or update a user document
 * @param {string} userId - The user ID
 * @param {string} email - The user's email
 * @param {string} displayName - The user's display name
 * @returns {Promise<void>}
 */
export const createOrUpdateUser = async (userId, email, displayName) => {
  try {
    const userRef = doc(db, getUserPath(userId));
    await setDoc(userRef, {
      email,
      displayName,
      createdAt: new Date(),
      updatedAt: new Date()
    }, { merge: true });
  } catch (error) {
    throw error;
  }
};

/**
 * Get user data by ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>} User data or null if not found
 */


/**
 * Add a project to a user's userProjects collection
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @param {string} projectName - The project name
 * @param {string} role - The user's role in the project ('owner', 'member', etc.)
 * @returns {Promise<void>}
 */
export const addUserProject = async (userId, projectId, projectName, role = 'member') => {
  try {
    const userProjectRef = doc(db, getUserProjectPath(userId, projectId));
    await setDoc(userProjectRef, {
      name: projectName,
      role,
      addedAt: new Date(),
      updatedAt: new Date()
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Remove a project from a user's userProjects collection
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @returns {Promise<void>}
 */
export const removeUserProject = async (userId, projectId) => {
  try {
    const userProjectRef = doc(db, getUserProjectPath(userId, projectId));
    await deleteDoc(userProjectRef);
  } catch (error) {
    throw error;
  }
};

/**
 * Update a user's role in a project
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @param {string} newRole - The new role
 * @returns {Promise<void>}
 */
export const updateUserProjectRole = async (userId, projectId, newRole) => {
  try {
    const userProjectRef = doc(db, getUserProjectPath(userId, projectId));
    await updateDoc(userProjectRef, {
      role: newRole,
      updatedAt: new Date()
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Get all projects a user has access to
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of user projects with project data
 */
export const getUserProjects = async (userId) => {
  try {
    const userProjectsRef = collection(db, getUserProjectsPath(userId));
    const userProjectsSnap = await getDocs(userProjectsRef);
    
    const userProjects = [];
    for (const userProjectDoc of userProjectsSnap.docs) {
      const userProjectData = userProjectDoc.data();
      
      // Get the actual project data
      const projectRef = doc(db, getProjectPath(userProjectDoc.id));
      const projectSnap = await getDoc(projectRef);
      
      if (projectSnap.exists()) {
        const projectData = projectSnap.data();
        userProjects.push({
          id: userProjectDoc.id,
          name: userProjectData.name,
          role: userProjectData.role,
          addedAt: userProjectData.addedAt,
          updatedAt: userProjectData.updatedAt,
          project: {
            id: projectSnap.id,
            ...projectData
          }
        });
      }
    }
    
    return userProjects;
  } catch (error) {
    throw error;
  }
};

/**
 * Check if a user has access to a specific project
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @returns {Promise<Object|null>} User project data or null if no access
 */


/**
 * Get all users who have access to a specific project
 * @param {string} projectId - The project ID
 * @returns {Promise<Array>} Array of users with their roles
 */


/**
 * Check if a user exists in Firebase Auth by email.
 * Uses Builder Service Admin SDK lookup because Firebase Client Auth
 * fetchSignInMethodsForEmail() returns [] when email enumeration protection is enabled.
 * @param {string} email - The user's email address
 * @param {string} projectId - Project ID used for owner/admin authorization
 * @returns {Promise<Object|null>} User existence status or null if not found
 */
export const getUserByEmail = async (email, projectId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    if (!projectId) {
      throw new Error('projectId is required');
    }

    const token = await currentUser.getIdToken();
    const baseUrl = import.meta.env.VITE_BUILDER_API_URL || 'http://localhost:8081';
    const normalizedEmail = email.trim().toLowerCase();

    const response = await fetch(`${baseUrl}/api/project-invitations/users/lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        projectId,
        email: normalizedEmail,
      }),
    });

    const body = await response.json().catch(() => ({}));

    if (response.status === 404 && body.code === 'USER_NOT_FOUND') {
      return null;
    }

    if (!response.ok) {
      const error = new Error(body.error || `Failed to look up user: ${response.status}`);
      error.code = body.code;
      error.status = response.status;
      throw error;
    }

    return body.data || {
      email: normalizedEmail,
      exists: true,
    };
  } catch (error) {
    throw error;
  }
};
